// Search: every record GovChew has collected, whether or not a feed matched it. Results with facets,
// removable filter chips, highlighting, sorting and paging; the home page when there is no query.
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, errorText } from "../api/client";
import {
  EXAMPLES,
  FACET_NAMES,
  FREQ,
  human,
  remember,
  recentSearches,
  resultsPath,
  searchPath,
  SORTS,
  stripFilters,
  suggestName,
  type FacetValue,
  type Frequency,
  type SearchResponse,
  type SearchResult,
  type Sort,
} from "../api/search";
import { useSelectedKey, useUI } from "../app/context";
import { keys, useSavedSearches, type SavedSearch } from "../app/queries";
import { Badge } from "../components/Badge";
import { GlobalSearch } from "../components/GlobalSearch";
import { Highlight } from "../components/Highlight";
import { DuePill, FundChip, RelatedList, RelatedToggle } from "../components/Rows";
import { SaveSearchDialog } from "../components/SaveSearchDialog";

const PAGE = 50;

/** Search pages use the page's wide layout. */
export function useWidePage() {
  useEffect(() => {
    const page = document.getElementById("page");
    page?.classList.add("wide");
    return () => page?.classList.remove("wide");
  }, []);
}

export function Search() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const sort = (params.get("sort") as Sort | null) ?? "best";
  useWidePage();
  if (!q) return <SearchHome focus={!!params.get("focus")} />;
  return <SearchResults key={q + "|" + sort} q={q} sort={sort} saved={null} />;
}

export function Tips() {
  return (
    <dl className="tip-list">
      <dt>
        <code>counter-uas training</code>
      </dt>
      <dd>All words, in any order. Common synonyms match too.</dd>
      <dt>
        <code>"live virtual constructive"</code>
      </dt>
      <dd>An exact phrase. Quoting one word turns synonyms off.</dd>
      <dt>
        <code>autonomy OR ai</code>
      </dt>
      <dd>Either word.</dd>
      <dt>
        <code>-janitorial</code>, <code>-buyer:army</code>
      </dt>
      <dd>Leave out a word or a filter.</dd>
      <dt>
        <code>sol:W912</code>, <code>naics:5417</code>, <code>psc:AJ</code>
      </dt>
      <dd>Solicitation number or code, by prefix.</dd>
      <dt>
        <code>won:leidos</code>
      </dt>
      <dd>Solicitations a company won, closed ones included.</dd>
      <dt>
        <code>buyer:navy stage:rfi type:baa topic:ai</code>
      </dt>
      <dd>Filters. Repeat one to allow either value.</dd>
      <dt>
        <code>due:30d</code>, <code>posted:7d</code>, <code>due:2026-10-01..2026-12-31</code>
      </dt>
      <dd>Due within, posted within, or a date range.</dd>
      <dt>
        <code>is:any</code>, <code>is:closed</code>, <code>is:solesource</code>, <code>in:title</code>
      </dt>
      <dd>Include closed or sole-source notices; search titles only.</dd>
    </dl>
  );
}

function SearchLinks({ q }: { q: string }) {
  const [tips, setTips] = useState(false);
  return (
    <>
      <div className="search-links">
        <Link to={q ? `/advanced?q=${encodeURIComponent(q)}` : "/advanced"}>Advanced search</Link>
        <button className="text-btn" id="tips-btn" aria-expanded={tips} onClick={() => setTips(!tips)}>
          Search tips
        </button>
      </div>
      <div className="tips" id="tips" hidden={!tips}>
        <Tips />
      </div>
    </>
  );
}

function SearchHome({ focus }: { focus: boolean }) {
  const saved = useSavedSearches().data ?? [];
  const browse = useQuery({ queryKey: ["search", "browse"], queryFn: () => api<SearchResponse>("/api/search?q=&limit=1") });
  const recent = recentSearches();
  const top = (name: string, n: number) =>
    (browse.data?.facets[name] ?? []).slice(0, n).map((v) => (
      <Link key={v.value} className="browse" to={resultsPath(v.query, null)}>
        <span>{v.label}</span>
        <span className="fc">{v.count}</span>
      </Link>
    ));
  return (
    <div className="search-home">
      <h1>Search</h1>
      <GlobalSearch value="" autoFocus={focus} />
      <SearchLinks q="" />
      <p className="examples">
        Try{" "}
        {EXAMPLES.map((e) => (
          <span key={e}>
            <Link to={searchPath(e)}>
              <code>{e}</code>
            </Link>{" "}
          </span>
        ))}
      </p>
      <div className="home-grid">
        <section>
          <h2>Your saved searches</h2>
          {saved.length ? (
            <ul className="plain">
              {saved.map((s) => (
                <li key={s.id}>
                  <Link to={`/saved/${s.id}`}>{s.name}</Link> <span className="muted-s">{s.query}</span>
                  {s.frequency !== "off" ? <span className="chip"> 🔔 {s.frequency === "daily" ? "Daily" : "As they arrive"}</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted-s">Save a search to come back to it, or to get alerts when something new matches.</p>
          )}
          {recent.length ? (
            <>
              <h2>Recent</h2>
              <ul className="plain">
                {recent.map((r) => (
                  <li key={r}>
                    <Link to={searchPath(r)}>{r}</Link>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
        <section>
          <h2>Browse by topic</h2>
          <div className="browse-list">{top("topic", 10)}</div>
        </section>
        <section>
          <h2>Browse by buyer</h2>
          <div className="browse-list">{top("buyer", 10)}</div>
          <h2>Closing soon</h2>
          <div className="browse-list">{top("due", 3)}</div>
        </section>
      </div>
    </div>
  );
}

/** Results for a query; inside a saved search, refinements stay attached so they can be saved back. */
export function SearchResults({ q, sort, saved }: { q: string; sort: Sort; saved: SavedSearch | null }) {
  const savedId = saved?.id ?? null;
  const path = (query: string, s: string | null = sort) => resultsPath(query, s, savedId);
  const [dialog, setDialog] = useState<Frequency | null>(null);
  const [facetsOpen, setFacetsOpen] = useState(false);
  const [tracked, setTracked] = useState<Record<string, boolean>>({});
  const { showToast } = useUI();
  const results = useInfiniteQuery({
    queryKey: ["search", q, sort],
    queryFn: ({ pageParam }) =>
      api<SearchResponse>(`/api/search?q=${encodeURIComponent(q)}&sort=${sort}&offset=${pageParam}&limit=${PAGE}`),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.total && last.items.length ? loaded : undefined;
    },
  });
  useEffect(() => {
    if (results.isSuccess) remember(q);
  }, [results.isSuccess, q]);

  const d = results.data?.pages[0];
  const items = results.data?.pages.flatMap((p) => p.items) ?? [];
  const toggleTrack = async (key: string, on: boolean) => {
    try {
      await api("/api/tracked/" + encodeURIComponent(key), { method: on ? "PUT" : "DELETE" });
      setTracked((t) => ({ ...t, [key]: on }));
      showToast(
        on
          ? "Tracking. You’ll get an alert if the due date, text, attachments or status change, and a week and a day before it’s due."
          : "Stopped tracking.",
      );
    } catch (err) {
      showToast(errorText(err));
    }
  };

  return (
    <>
      <div className="search-head">
        {saved ? (
          <>
            <div className="date">Saved search</div>
            <h1 className="saved-title">{saved.name}</h1>
          </>
        ) : null}
        <GlobalSearch value={q} />
        <SearchLinks q={q} />
      </div>
      {results.isError ? (
        <p className="empty">{errorText(results.error, "Search failed. Try again.")}</p>
      ) : !d ? (
        <div className="search-grid">
          <aside className="facets" id="facets" aria-label="Filters" />
          <div>
            <div id="results">
              <p className="empty">Searching…</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="chips" id="chips">
            {d.chips.map((c) => (
              <Link key={c.name + c.value + c.negated} className="chip-f" to={path(c.query)} title="Remove this filter">
                <span>
                  {FACET_NAMES[c.name] ?? ""}
                  {FACET_NAMES[c.name] ? ": " : ""}
                  {c.label}
                </span>
                <span aria-hidden="true">×</span>
                <span className="sr">Remove</span>
              </Link>
            ))}
            {d.chips.length > 1 ? (
              <Link className="text-btn" to={path(stripFilters(q))}>
                Clear filters
              </Link>
            ) : null}
          </div>
          <div className="results-bar" id="results-bar">
            <div>
              <b>{d.total.toLocaleString()}</b> result{d.total === 1 ? "" : "s"}
            </div>
            <SortButtons d={d} path={path} q={q} />
            <span className="spacer" />
            {saved ? (
              <SavedControls saved={saved} q={q} sort={sort} />
            ) : (
              <>
                <button className="btn" id="save-search" onClick={() => setDialog("off")}>
                  ☆ Save search
                </button>
                <button className="btn" id="alert-search" onClick={() => setDialog("daily")}>
                  🔔 Alert me
                </button>
              </>
            )}
            <button className="btn mobile-only" id="show-facets" aria-expanded={facetsOpen} onClick={() => setFacetsOpen(!facetsOpen)}>
              Filters{d.chips.length ? ` (${d.chips.length})` : ""}
            </button>
          </div>
          <HiddenNote d={d} path={path} />
          <div className="search-grid">
            <aside className={`facets ${facetsOpen ? "open" : ""}`} id="facets" aria-label="Filters">
              <Facets d={d} path={path} />
            </aside>
            <div>
              <div id="results">
                {items.length ? (
                  <>
                    <div className="results">
                      {items.map((n) => (
                        <ResultRow
                          key={n.key}
                          n={n}
                          terms={d.highlight}
                          tracked={tracked[n.key] ?? n.tracked}
                          onTrack={(on) => void toggleTrack(n.key, on)}
                        />
                      ))}
                    </div>
                    {results.hasNextPage ? (
                      <button className="btn more" id="more" disabled={results.isFetchingNextPage} onClick={() => void results.fetchNextPage()}>
                        {results.isFetchingNextPage ? "Loading…" : `Show more (${(d.total - items.length).toLocaleString()} left)`}
                      </button>
                    ) : null}
                  </>
                ) : (
                  <NoResults d={d} path={path} />
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {dialog ? (
        <SaveSearchDialog open onClose={() => setDialog(null)} query={q} sort={sort} frequency={dialog} name={suggestName(q, d?.chips)} />
      ) : null}
    </>
  );
}

function SortButtons({ d, path, q }: { d: SearchResponse; path: (q: string, s?: string | null) => string; q: string }) {
  const navigate = useNavigate();
  const buttons = d.has_text ? SORTS : SORTS.filter(([v]) => v !== "best");
  return (
    <div className="seg" role="group" aria-label="Sort">
      {buttons.map(([v, l]) => (
        <button key={v} data-sort={v} aria-pressed={d.sort === v} onClick={() => navigate(path(q, v))}>
          {l}
        </button>
      ))}
    </div>
  );
}

function HiddenNote({ d, path }: { d: SearchResponse; path: (q: string) => string }) {
  const notes = [];
  if (d.hidden.closed.count)
    notes.push(
      <Link key="closed" to={path(d.hidden.closed.query)}>
        {d.hidden.closed.count.toLocaleString()} closed or awarded
      </Link>,
    );
  if (d.hidden.sole.count)
    notes.push(
      <Link key="sole" to={path(d.hidden.sole.query)}>
        {d.hidden.sole.count} intent-to-sole-source
      </Link>,
    );
  return (
    <div className="hidden-note" id="hidden-note">
      {notes.length ? (
        <>
          Also matching but not shown: {notes[0]}
          {notes[1] ? <> and {notes[1]}</> : null}. Select to include them.
        </>
      ) : null}
    </div>
  );
}

function Facets({ d, path }: { d: SearchResponse; path: (q: string) => string }) {
  const sections = Object.entries(FACET_NAMES)
    .map(([name, title]) => [name, title, d.facets[name] ?? []] as const)
    .filter(([, , values]) => values.length);
  if (!sections.length) return <p className="muted">No filters apply.</p>;
  return (
    <>
      {sections.map(([name, title, values]) => (
        <FacetSection key={name} title={title} values={values} path={path} />
      ))}
    </>
  );
}

function FacetSection({ title, values, path }: { title: string; values: FacetValue[]; path: (q: string) => string }) {
  const [more, setMore] = useState(false);
  const limit = 6;
  const extra = values.filter((v, i) => i >= limit && !v.active).length;
  return (
    <section className="facet">
      <h3>{title}</h3>
      <ul>
        {values.map((v, i) => (
          <li key={v.value} className={i >= limit && !v.active ? "extra" : undefined} hidden={i >= limit && !v.active && !more}>
            <Link to={path(v.query)} role="checkbox" aria-checked={v.active} className={v.count || v.active ? "" : "zero"}>
              <span className="box" aria-hidden="true" />
              <span className="fl">{v.label}</span>
              <span className="fc">{v.count.toLocaleString()}</span>
            </Link>
          </li>
        ))}
      </ul>
      {values.filter((v) => !v.active).length > limit ? (
        <button className="text-btn facet-more" aria-expanded={more} onClick={() => setMore(!more)}>
          {more ? "Show fewer" : `Show ${extra} more`}
        </button>
      ) : null}
    </section>
  );
}

function ResultRow({ n, terms, tracked, onTrack }: { n: SearchResult; terms: string[]; tracked: boolean; onTrack: (on: boolean) => void }) {
  const { openPanel } = useUI();
  const selected = useSelectedKey() === n.key;
  const open = () => openPanel({ kind: "notice", key: n.key, feedId: null });
  const [expanded, setExpanded] = useState(false);
  const meta = [
    n.buyer,
    n.work_label ? <span className="shape-tag">{n.work_label}</span> : null,
    n.path_label || n.kind,
    n.solicitation_id ? (
      <span className="mono">
        <Highlight text={n.solicitation_id} terms={terms} />
      </span>
    ) : null,
    <>
      {n.source_label}
      {n.also?.length ? <span className="also"> + {n.also.join(", ")}</span> : null}
    </>,
    n.funding ? <FundChip f={n.funding} /> : null,
    n.sole_source ? <span className="chip warn">Intent to sole source</span> : null,
    n.status && /closed|award|cancel|archiv|inactive/i.test(n.status) ? <span className="chip">{human(n.status)}</span> : null,
    n.related?.length ? <RelatedToggle n={n} open={expanded} onToggle={() => setExpanded(!expanded)} /> : null,
  ].filter(Boolean);
  return (
    <>
      <article
        className="result"
        data-key={n.key}
        tabIndex={0}
        role="button"
        aria-selected={selected}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target === e.currentTarget) open();
        }}
      >
        <Badge agency={n.agency} small />
        <div className="r-main">
          <h3>
            <Highlight text={n.title} terms={terms} />
          </h3>
          <div className="r-meta">
            {meta.map((m, i) => (
              <span key={i}>
                {i ? <span className="dot">·</span> : null}
                {m}
              </span>
            ))}
          </div>
          {n.snippet ? (
            <p className="r-snip">
              <Highlight text={n.snippet} terms={terms} />
            </p>
          ) : null}
        </div>
        <div className="r-side">
          <DuePill n={n} />
          <button
            className="bell"
            data-track={n.key}
            aria-pressed={tracked}
            title={tracked ? "Tracking: you get alerts when it changes" : "Track changes to this opportunity"}
            onClick={(e) => {
              e.stopPropagation();
              onTrack(!tracked);
            }}
          >
            {tracked ? "🔔 Tracking" : "🔔 Track"}
          </button>
        </div>
      </article>
      {expanded ? <RelatedList n={n} feedId={null} /> : null}
    </>
  );
}

function NoResults({ d, path }: { d: SearchResponse; path: (q: string) => string }) {
  return (
    <div className="no-results">
      <h2>No results for “{d.query}”</h2>
      <ul>
        {d.chips.length ? (
          <li>
            Remove a filter:{" "}
            {d.chips.map((c, i) => (
              <span key={c.name + c.value}>
                {i ? ", " : ""}
                <Link to={path(c.query)}>{c.label}</Link>
              </span>
            ))}
          </li>
        ) : null}
        {d.hidden.closed.count ? (
          <li>
            <Link to={path(d.hidden.closed.query)}>Include {d.hidden.closed.count} closed or awarded</Link>
          </li>
        ) : null}
        {d.has_text ? <li>Use fewer or broader words; GovChew already matches common synonyms (UAS, unmanned, drone).</li> : null}
        <li>
          Search a solicitation number directly, e.g. <code>W912</code> or <code>HR0011</code>.
        </li>
      </ul>
    </div>
  );
}

/** Inside a saved search: save refinements back, set its alert schedule, rename or delete it. */
function SavedControls({ saved, q, sort }: { saved: SavedSearch; q: string; sort: Sort }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useUI();
  const id = saved.id;
  const changed = q !== saved.query || sort !== (saved.sort || "best");
  const refresh = () => client.invalidateQueries({ queryKey: keys.searches });
  const patch = (body: Record<string, unknown>) => api(`/api/searches/${id}`, { method: "PATCH", body });
  const run = (f: () => Promise<void>) => () => void f().catch((err: unknown) => showToast(errorText(err)));
  return (
    <>
      {changed ? (
        <button
          className="btn primary"
          id="update-saved"
          onClick={run(async () => {
            await patch({ query: q, sort });
            await refresh();
            showToast("Saved search updated.");
            navigate(`/saved/${id}`);
          })}
        >
          Update saved search
        </button>
      ) : null}
      <label className="alert-pick">
        🔔{" "}
        <select
          id="saved-freq"
          aria-label="Alerts for this search"
          value={saved.frequency}
          onChange={(e) => {
            const f = e.target.value as Frequency;
            run(async () => {
              await patch({ frequency: f });
              await refresh();
              showToast(f === "off" ? "Alerts off for this search." : `You'll get alerts ${f === "daily" ? "in a daily digest" : "as new records arrive"}.`);
            })();
          }}
        >
          {FREQ.map(([v, l]) => (
            <option key={v} value={v}>
              {v === "off" ? "Alerts off" : l}
            </option>
          ))}
        </select>
      </label>
      <button
        className="btn"
        id="saved-to-feed"
        title="A feed holding what this search finds, which learns from what you mark in it"
        onClick={run(async () => {
          const r = await api<{ id: number }>("/api/feeds", { method: "POST", body: { name: saved.name, query: q } });
          await client.invalidateQueries({ queryKey: keys.feeds });
          navigate(`/feeds/${r.id}?view=all`);
          showToast(`Created the feed ${saved.name}. The saved search stays too.`);
        })}
      >
        Make it a feed
      </button>
      <button
        className="btn"
        id="edit-saved"
        onClick={run(async () => {
          const name = window.prompt("Name this search", saved.name);
          if (!name?.trim()) return;
          await patch({ name: name.trim() });
          await refresh();
        })}
      >
        Rename…
      </button>
      <button
        className="btn danger"
        id="delete-saved"
        onClick={run(async () => {
          if (!window.confirm(`Delete the saved search “${saved.name}”? Its alerts stop too.`)) return;
          await api(`/api/searches/${id}`, { method: "DELETE" });
          await refresh();
          navigate(searchPath(q, sort));
          showToast("Saved search deleted.");
        })}
      >
        Delete
      </button>
    </>
  );
}
